import { useEffect, useState, useCallback } from "react";
import { Truck, Check, X, Cpu, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmt } from "@/lib/api";
import { ORDER_STATUS_LABEL as STATUS_LABEL, orderStatusBadgeClass, orderStatusTextClass, isOrderInProgress } from "@/lib/orderStatus";
import PulseDot from "@/components/common/PulseDot";

const NEXT_STATUS = { accepted: "making", making: "delivery", delivery: "delivered" };

export default function AdminOrders() {
  const [tenants, setTenants] = useState([]);
  const [tenantFilter, setTenantFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [orders, setOrders] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/tenants").then((r) => setTenants(r.data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (tenantFilter) params.set("tenant_id", tenantFilter);
    if (statusFilter !== "all") params.set("status", statusFilter);
    api.get(`/admin/package-requests${params.toString() ? `?${params}` : ""}`).then((r) => setOrders(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [tenantFilter, statusFilter]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const current = orders.find((o) => o.id === selectedId) || orders[0] || null;
  const p = current?.package;

  const setStatus = async (status, note) => {
    setBusy(true);
    try {
      await api.patch(`/admin/package-requests/${current.id}`, { status, note });
      toast.success(`Order marked ${STATUS_LABEL[status].toLowerCase()}`);
      setRejecting(false); setRejectReason("");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="h-full grid grid-cols-[380px_1fr]" data-testid="admin-orders-page">
      <aside className="border-r border-kl-lineLight flex flex-col bg-kl-light">
        <div className="p-6 border-b border-kl-lineLight">
          <p className="kl-label">Hardware</p>
          <h1 className="font-display font-light text-2xl mt-1">Orders <span className="font-mono text-sm text-kl-mutedLight">{orders.length}</span></h1>
          <div className="flex gap-2 mt-3">
            <select value={tenantFilter} onChange={(e) => setTenantFilter(e.target.value)} className="kl-input-box h-9 flex-1" data-testid="admin-orders-tenant-filter">
              <option value="">All dealers</option>
              {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="kl-input-box h-9 w-32 shrink-0" data-testid="admin-orders-status-filter">
              <option value="all">All</option>
              {Object.entries(STATUS_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
        </div>
        <ul className="flex-1 overflow-y-auto kl-scroll-light">
          {orders.length === 0 && <li className="p-6 text-sm text-kl-mutedLight" data-testid="admin-orders-empty">No requests match.</li>}
          {orders.map((o) => (
            <li key={o.id} className={`border-b border-kl-lineLight ${current?.id === o.id ? "bg-white" : "hover:bg-white"} transition-colors`} data-testid={`admin-order-row-${o.id}`}>
              <button onClick={() => setSelectedId(o.id)} className="w-full text-left p-4">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-display text-sm truncate">{o.client_name}</span>
                  <span className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${o.status === "pending" ? "border-kl-green text-kl-greenDark" : orderStatusBadgeClass(o.status)}`}>
                    {isOrderInProgress(o.status) && <PulseDot size="h-1.5 w-1.5" />}
                    {STATUS_LABEL[o.status] || o.status}
                  </span>
                </div>
                <div className="text-[11px] text-kl-mutedLight mt-1 flex items-center gap-1.5"><Building2 size={11} /> {o.tenant_name || "—"}</div>
                <div className="font-mono text-[11px] text-kl-mutedLight mt-1 truncate">{o.serial} · {fmt.date(o.requested_at)}</div>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <section className="overflow-y-auto kl-scroll-light">
        {current ? (
          <div className="max-w-3xl mx-auto p-8" data-testid="admin-order-detail">
            <p className="kl-label">{current.tenant_name} · {current.serial} · requested {fmt.time(current.requested_at)} by {current.requested_by}</p>
            <h2 className="font-display font-light text-3xl mt-1">{current.client_name}</h2>

            {p && (
              <div className="kl-panel-dark p-5 mt-6">
                <p className="kl-label-dark flex items-center gap-1.5"><Cpu size={12} /> Requested package</p>
                <div className="mt-3 space-y-2 font-mono text-sm">
                  <div className="flex justify-between"><span className="text-kl-muted">Panel</span><span>{p.panel.qty} × {p.panel.brand} {p.panel.model} ({p.panel.rating} {p.panel.unit})</span></div>
                  <div className="flex justify-between"><span className="text-kl-muted">Inverter</span><span>{p.inverter.qty} × {p.inverter.brand} {p.inverter.model} ({p.inverter.rating} {p.inverter.unit})</span></div>
                  <div className="flex justify-between"><span className="text-kl-muted">Battery</span><span>{p.battery.qty} × {p.battery.brand} {p.battery.model} ({p.battery.rating} {p.battery.unit})</span></div>
                  <div className="flex justify-between border-t border-kl-line pt-2 mt-2"><span className="text-kl-muted">System cost</span><span className="text-kl-green">{fmt.money(p.system_cost, p.currency)}</span></div>
                </div>
              </div>
            )}

            <div className="kl-panel p-6 mt-4">
              <p className="kl-label mb-4">Status</p>
              {current.status === "pending" ? (
                <div className="flex gap-3">
                  <button onClick={() => setStatus("accepted")} disabled={busy} className="kl-btn-primary flex-1" data-testid="admin-order-accept"><Check size={15} /> Accept</button>
                  <button onClick={() => setRejecting(true)} disabled={busy} className="kl-btn-outline flex-1 hover:bg-kl-inactive hover:border-kl-inactive hover:text-white" data-testid="admin-order-reject-open"><X size={15} /> Reject</button>
                </div>
              ) : current.status === "rejected" ? (
                <p className="text-sm text-kl-inactive">Rejected{current.reject_reason ? ` — ${current.reject_reason}` : ""}</p>
              ) : current.status === "delivered" ? (
                <p className="text-sm text-kl-greenDark font-display">Delivered — order complete.</p>
              ) : (
                <div className="flex items-center gap-3">
                  <PulseDot size="h-2.5 w-2.5" />
                  <span className={`font-display text-lg ${orderStatusTextClass(current.status)}`}>{STATUS_LABEL[current.status]}</span>
                  {NEXT_STATUS[current.status] && (
                    <button onClick={() => setStatus(NEXT_STATUS[current.status])} disabled={busy} className="kl-btn-outline ml-auto" data-testid="admin-order-advance">
                      Mark as {STATUS_LABEL[NEXT_STATUS[current.status]]}
                    </button>
                  )}
                </div>
              )}

              {rejecting && (
                <form onSubmit={(e) => { e.preventDefault(); setStatus("rejected", rejectReason); }} className="mt-4 pt-4 border-t border-kl-lineLight">
                  <span className="kl-label">Reason (optional)</span>
                  <input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} className="kl-input-box mt-1 w-full" placeholder="e.g. Out of stock" data-testid="admin-order-reject-reason" />
                  <div className="flex justify-end gap-2 mt-3">
                    <button type="button" onClick={() => setRejecting(false)} className="kl-btn-outline h-9 px-3 text-xs">Cancel</button>
                    <button disabled={busy} className="kl-btn-primary h-9 px-3 text-xs bg-kl-inactive hover:bg-red-600" data-testid="admin-order-reject-submit">{busy ? "Rejecting…" : "Confirm reject"}</button>
                  </div>
                </form>
              )}
            </div>

            <div className="kl-panel p-5 mt-4">
              <p className="kl-label flex items-center gap-1.5"><Truck size={12} /> Status history</p>
              <ul className="mt-3 space-y-2">
                {current.status_log?.slice().reverse().map((e, i) => (
                  <li key={i} className="text-xs"><span className="font-display capitalize">{e.status}</span><span className="text-kl-mutedLight"> · {fmt.time(e.at)} · by {e.by}</span>{e.note && <div className="text-kl-mutedLight mt-0.5">{e.note}</div>}</li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-center p-10" data-testid="admin-orders-empty-state">
            <Truck size={28} className="text-kl-mutedLight" />
            <h2 className="font-display text-2xl mt-4">No package requests</h2>
            <p className="text-sm text-kl-mutedLight mt-1 max-w-sm">When a dealer requests a package for a proposal, it shows up here for approval.</p>
          </div>
        )}
      </section>
    </div>
  );
}
